import React, { useState } from 'react';
import {
  Building2, DollarSign, Phone, Mail, MapPin, Clock, Globe,
  ArrowRight, ArrowLeft, Check, Sparkles, CreditCard, ChevronRight, Palette, CalendarDays,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SITE_URL, SITE_NAME } from '../lib/config';
import BrandingStep from './BrandingStep';

interface LotOwnerSignupForm {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  lotName: string;
  lotSlug: string;
  address: string;
  description: string;
  hourlyRate: number;
  maxDailyRate: number;
  maxHours: number;
  monthlyEnabled: boolean;
  subscriptionAmount: number;
  logoUrl: string;
  brandColor: string;
}

const STEPS = [
  { id: 1, label: 'You', icon: Mail },
  { id: 2, label: 'Your Lot', icon: MapPin },
  { id: 3, label: 'Pricing', icon: DollarSign },
  { id: 4, label: 'Branding', icon: Palette },
  { id: 5, label: 'Review', icon: Check },
];

const SITEHOST = (() => {
  try { return new URL(SITE_URL).hostname; } catch { return 'parkconfetti.com'; }
})();

export default function LotOwnerSignup() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [formData, setFormData] = useState<LotOwnerSignupForm>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    lotName: '',
    lotSlug: '',
    address: '',
    description: '',
    hourlyRate: 4.50,
    maxDailyRate: 18.00,
    maxHours: 12,
    monthlyEnabled: true,
    subscriptionAmount: 150.00,
    logoUrl: '',
    brandColor: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleLotNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lotName = e.target.value;
    const autoSlug = lotName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .trim();
    setFormData(prev => ({
      ...prev,
      lotName,
      lotSlug: slugManuallyEdited ? prev.lotSlug : autoSlug,
    }));
  };

  const handleLotSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugManuallyEdited(true);
    setFormData(prev => ({ ...prev, lotSlug: e.target.value }));
  };

  const validateStep = (s: number): boolean => {
    if (s === 1) {
      if (!formData.businessName.trim()) { toast.error('Business name is required'); return false; }
      if (!formData.contactName.trim()) { toast.error('Contact name is required'); return false; }
      if (!formData.email.trim()) { toast.error('Email is required'); return false; }
      if (!formData.phone.trim()) { toast.error('Phone is required'); return false; }
    }
    if (s === 2) {
      if (!formData.lotName.trim()) { toast.error('Lot name is required'); return false; }
      if (!formData.lotSlug.trim()) { toast.error('URL slug is required'); return false; }
      if (!/^[a-z0-9\-]+$/.test(formData.lotSlug)) {
        toast.error('Slug can only contain lowercase letters, numbers, and hyphens');
        return false;
      }
      if (!formData.address.trim()) { toast.error('Address is required'); return false; }
    }
    if (s === 3) {
      if (formData.hourlyRate <= 0) { toast.error('Hourly rate must be greater than 0'); return false; }
      if (formData.maxDailyRate <= 0) { toast.error('Max daily rate must be greater than 0'); return false; }
      if (formData.maxHours < 1) { toast.error('Max hours must be at least 1'); return false; }
    }
    // Step 4 (Branding) is optional — nothing to validate
    return true;
  };

  const next = () => {
    if (validateStep(step)) setStep(s => Math.min(s + 1, 5));
  };

  const back = () => setStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    setLoading(true);
    try {
      const apiUrl = window.location.hostname === 'localhost'
        ? '/api/onboard-lot-owner'
        : '/.netlify/functions/onboard-lot-owner';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to create lot');
      }

      const result = await response.json();
      toast.success('Lot created! Redirecting to Stripe...');
      setTimeout(() => { window.location.href = result.onboardingUrl; }, 1500);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create lot');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-white border border-ink-300 rounded-xl text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-confetti-pink focus:border-confetti-pink transition-all';
  const labelClass = 'block text-sm font-semibold text-ink-700 mb-1.5';

  return (
    <div className="min-h-screen bg-cream-100 flex flex-col items-center justify-center py-10 px-4">
      {/* Header wordmark */}
      <div className="mb-8 flex items-center gap-2">
        <div className="h-9 w-9 bg-confetti-pink rounded-xl flex items-center justify-center shadow-confetti">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <span className="font-display text-xl font-bold text-ink-900">{SITE_NAME}</span>
      </div>

      <div className="w-full max-w-lg">
        {/* Step progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between relative">
            {/* connecting line */}
            <div className="absolute inset-0 flex items-center px-4 top-5 pointer-events-none">
              <div className="w-full h-px bg-ink-300" />
            </div>
            {STEPS.map((s) => {
              const Icon = s.icon;
              const done = step > s.id;
              const active = step === s.id;
              return (
                <div key={s.id} className="relative flex flex-col items-center gap-1.5 z-10">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      done
                        ? 'bg-confetti-pink border-confetti-pink'
                        : active
                        ? 'bg-white border-confetti-pink shadow-confetti'
                        : 'bg-white border-ink-300'
                    }`}
                  >
                    {done ? (
                      <Check className="h-4 w-4 text-white" />
                    ) : (
                      <Icon className={`h-4 w-4 ${active ? 'text-confetti-pink' : 'text-ink-400'}`} />
                    )}
                  </div>
                  <span
                    className={`text-xs font-semibold tracking-wide ${
                      active ? 'text-confetti-pink' : done ? 'text-ink-700' : 'text-ink-400'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lift overflow-hidden">
          {/* Step content */}
          <div className="px-8 pt-8 pb-6">

            {/* ── Step 1: About You ── */}
            {step === 1 && (
              <div>
                <StepHeading
                  tag="Step 1 of 5"
                  title="Tell us about yourself"
                  subtitle="We'll use this to set up your account and send important notifications."
                />
                <div className="space-y-4 mt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Business Name</label>
                      <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="ABC Parking"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Your Name</label>
                      <input
                        type="text"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Jane Smith"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>
                      <Mail className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="jane@abcparking.com"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      <Phone className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 2: Your Lot ── */}
            {step === 2 && (
              <div>
                <StepHeading
                  tag="Step 2 of 5"
                  title="Set up your parking lot"
                  subtitle="Give your lot a name and a public URL that customers will use."
                />
                <div className="space-y-4 mt-6">
                  <div>
                    <label className={labelClass}>Lot Name</label>
                    <input
                      type="text"
                      name="lotName"
                      value={formData.lotName}
                      onChange={handleLotNameChange}
                      className={inputClass}
                      placeholder="Downtown Parking Lot"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      <Globe className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                      Public URL
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-ink-300 focus-within:ring-2 focus-within:ring-confetti-pink focus-within:border-confetti-pink transition-all">
                      <span className="flex items-center px-3 bg-cream-100 text-ink-500 text-sm border-r border-ink-300 whitespace-nowrap">
                        {SITEHOST}/lots/
                      </span>
                      <input
                        type="text"
                        name="lotSlug"
                        value={formData.lotSlug}
                        onChange={handleLotSlugChange}
                        className="flex-1 px-3 py-3 bg-white text-ink-900 placeholder-ink-400 focus:outline-none text-sm"
                        placeholder="downtown-parking"
                        pattern="^[a-z0-9\-]+$"
                      />
                    </div>
                    <p className="text-xs text-ink-400 mt-1.5">Lowercase letters, numbers, and hyphens only.</p>
                  </div>
                  <div>
                    <label className={labelClass}>
                      <MapPin className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="123 Main St, Toronto, ON M5V 3A8"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Description <span className="font-normal text-ink-400">(optional)</span></label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className={inputClass + ' resize-none'}
                      placeholder="Convenient downtown parking with easy access…"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 3: Pricing ── */}
            {step === 3 && (
              <div>
                <StepHeading
                  tag="Step 3 of 5"
                  title="Set your pricing"
                  subtitle="You can always adjust these later from your dashboard."
                />
                <div className="mt-6 space-y-4">
                  <PricingCard
                    icon={<DollarSign className="h-5 w-5 text-confetti-pink" />}
                    label="Hourly Rate"
                    sublabel="CAD per hour"
                    name="hourlyRate"
                    value={formData.hourlyRate}
                    onChange={handleChange}
                    step="0.25"
                    min="0"
                  />
                  <PricingCard
                    icon={<DollarSign className="h-5 w-5 text-confetti-blue" />}
                    label="Max Daily Rate"
                    sublabel="CAD cap per day"
                    name="maxDailyRate"
                    value={formData.maxDailyRate}
                    onChange={handleChange}
                    step="0.25"
                    min="0"
                  />
                  <PricingCard
                    icon={<Clock className="h-5 w-5 text-confetti-coral" />}
                    label="Max Hours"
                    sublabel="Maximum stay duration"
                    name="maxHours"
                    value={formData.maxHours}
                    onChange={handleChange}
                    step="1"
                    min="1"
                    max="24"
                    suffix="hrs"
                  />

                  {/* Monthly parking toggle */}
                  <div className={`rounded-xl border transition-colors ${formData.monthlyEnabled ? 'border-confetti-pink/40 bg-white' : 'border-ink-300 bg-cream-100'}`}>
                    <div className="flex items-center justify-between gap-4 p-4">
                      <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shadow-soft shrink-0 transition-colors ${formData.monthlyEnabled ? 'bg-confetti-pink' : 'bg-white border border-ink-300'}`}>
                          <CalendarDays className={`h-5 w-5 ${formData.monthlyEnabled ? 'text-white' : 'text-ink-400'}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-ink-900">Monthly Parking</p>
                          <p className="text-xs text-ink-400">Offer monthly subscriptions to regulars</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, monthlyEnabled: !prev.monthlyEnabled }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-confetti-pink focus:ring-offset-2 ${formData.monthlyEnabled ? 'bg-confetti-pink' : 'bg-ink-300'}`}
                        role="switch"
                        aria-checked={formData.monthlyEnabled}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.monthlyEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {formData.monthlyEnabled && (
                      <div className="px-4 pb-4 border-t border-ink-300/50 pt-3">
                        <PricingCard
                          icon={<CalendarDays className="h-5 w-5 text-confetti-pink" />}
                          label="Monthly Rate"
                          sublabel="CAD per month"
                          name="subscriptionAmount"
                          value={formData.subscriptionAmount}
                          onChange={handleChange}
                          step="5"
                          min="0"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-5 p-4 bg-confetti-lime-soft rounded-xl flex items-start gap-3">
                  <Sparkles className="h-4 w-4 text-ink-700 mt-0.5 shrink-0" />
                  <p className="text-sm text-ink-700">
                    ParkConfetti charges a <strong>10% platform fee</strong> on all sessions. Everything else goes directly to your bank account via Stripe.
                  </p>
                </div>
              </div>
            )}

            {/* ── Step 4: Branding ── */}
            {step === 4 && (
              <div>
                <StepHeading
                  tag="Step 4 of 5"
                  title="Brand your lot page"
                  subtitle="Add your logo and pick a color. Totally optional — you can always update this from your dashboard."
                />
                <div className="mt-6">
                  <BrandingStep
                    lotName={formData.lotName}
                    logoUrl={formData.logoUrl}
                    brandColor={formData.brandColor}
                    onLogoChange={(url) => setFormData(prev => ({ ...prev, logoUrl: url }))}
                    onColorChange={(color) => setFormData(prev => ({ ...prev, brandColor: color }))}
                    allowUpload={true}
                  />
                </div>
              </div>
            )}

            {/* ── Step 5: Review ── */}
            {step === 5 && (
              <div>
                <StepHeading
                  tag="Step 5 of 5"
                  title="Review & go live"
                  subtitle="Everything look good? Hit submit and we'll set up your Stripe account."
                />
                <div className="mt-6 space-y-3">
                  <ReviewSection title="About You">
                    <ReviewRow label="Business" value={formData.businessName} />
                    <ReviewRow label="Contact" value={formData.contactName} />
                    <ReviewRow label="Email" value={formData.email} />
                    <ReviewRow label="Phone" value={formData.phone} />
                  </ReviewSection>
                  <ReviewSection title="Your Lot">
                    <ReviewRow label="Name" value={formData.lotName} />
                    <ReviewRow label="URL" value={`${SITEHOST}/lots/${formData.lotSlug}`} mono />
                    <ReviewRow label="Address" value={formData.address} />
                    {formData.description && <ReviewRow label="Description" value={formData.description} />}
                  </ReviewSection>
                  <ReviewSection title="Pricing">
                    <ReviewRow label="Hourly" value={`$${formData.hourlyRate.toFixed(2)} CAD`} />
                    <ReviewRow label="Daily Max" value={`$${formData.maxDailyRate.toFixed(2)} CAD`} />
                    <ReviewRow label="Max Hours" value={`${formData.maxHours} hrs`} />
                    <ReviewRow label="Monthly" value={formData.monthlyEnabled ? `$${formData.subscriptionAmount.toFixed(2)} CAD/mo` : 'Disabled'} />
                  </ReviewSection>
                  {(formData.logoUrl || formData.brandColor) && (
                    <ReviewSection title="Branding">
                      {formData.logoUrl && <ReviewRow label="Logo URL" value={formData.logoUrl} mono />}
                      {formData.brandColor && <ReviewRow label="Brand Color" value={formData.brandColor} mono />}
                    </ReviewSection>
                  )}
                </div>
                <div className="mt-5 flex items-center gap-3 p-4 bg-confetti-blue-soft rounded-xl">
                  <CreditCard className="h-5 w-5 text-confetti-blue shrink-0" />
                  <p className="text-sm text-ink-700">
                    After submitting you'll be redirected to <strong>Stripe</strong> to connect your bank account.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer nav */}
          <div className="px-8 py-5 bg-cream-50 border-t border-ink-300 flex items-center justify-between gap-4">
            {step > 1 ? (
              <button
                onClick={back}
                className="flex items-center gap-1.5 text-sm font-semibold text-ink-500 hover:text-ink-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            ) : (
              <div />
            )}

            {step < 5 ? (
              <button
                onClick={next}
                className="ml-auto flex items-center gap-2 bg-confetti-pink text-white px-6 py-3 rounded-xl font-semibold hover:bg-confetti-pink/90 focus:ring-2 focus:ring-confetti-pink focus:ring-offset-2 transition-all shadow-confetti"
              >
                {step === 4 ? 'Review' : 'Continue'}
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="ml-auto flex items-center gap-2 bg-confetti-pink text-white px-6 py-3 rounded-xl font-semibold hover:bg-confetti-pink/90 focus:ring-2 focus:ring-confetti-pink focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-confetti"
              >
                {loading ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Creating your lot…
                  </>
                ) : (
                  <>
                    Create Lot & Set Up Payments
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-ink-400 mt-5">
          By continuing you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StepHeading({ tag, title, subtitle }: { tag: string; title: string; subtitle: string }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-widest text-confetti-pink mb-1">{tag}</p>
      <h1 className="text-2xl font-display font-bold text-ink-900">{title}</h1>
      <p className="text-sm text-ink-500 mt-1">{subtitle}</p>
    </div>
  );
}

function PricingCard({
  icon, label, sublabel, name, value, onChange, step, min, max, suffix,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  name: string;
  value: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  step: string;
  min: string;
  max?: string;
  suffix?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 bg-cream-100 rounded-xl border border-ink-300 hover:border-confetti-pink/40 transition-colors">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 bg-white rounded-lg flex items-center justify-center shadow-soft shrink-0">
          {icon}
        </div>
        <div>
          <p className="font-semibold text-sm text-ink-900">{label}</p>
          <p className="text-xs text-ink-400">{sublabel}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {!suffix && <span className="text-ink-400 text-sm font-medium">$</span>}
        <input
          type="number"
          name={name}
          value={value}
          onChange={onChange}
          step={step}
          min={min}
          max={max}
          className="w-20 px-2 py-2 text-right bg-white border border-ink-300 rounded-lg text-sm font-semibold text-ink-900 focus:outline-none focus:ring-2 focus:ring-confetti-pink transition-all"
        />
        {suffix && <span className="text-ink-400 text-sm font-medium">{suffix}</span>}
      </div>
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-300 overflow-hidden">
      <div className="px-4 py-2.5 bg-cream-100 border-b border-ink-300">
        <p className="text-xs font-bold uppercase tracking-widest text-ink-500">{title}</p>
      </div>
      <div className="divide-y divide-ink-300">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <span className="text-sm text-ink-400 shrink-0">{label}</span>
      <span className={`text-sm text-right text-ink-900 font-medium ${mono ? 'font-mono text-xs bg-cream-100 px-2 py-0.5 rounded' : ''}`}>
        {value}
      </span>
    </div>
  );
}
